import type { Field, TypeInstanceMap } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    type CallActionConfig,
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    innerTypeToFieldTypeMapInCondition,
    isRichTextValue,
    useAtomData
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { textRichVariableName } from '../util'
import * as SC from './styles'

type CallCardProps = {
    config: CallActionConfig
}

const CallCard: React.FC<CallCardProps> = ({ config }) => {
    const { phone } = config
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const nodeId = useMemo(() => {
        if (phone.type === 'UPSTREAM') {
            const nodeId = phone?.upstreamVariable?.nodeId
            if (nodeId) {
                return nodeId
            }
        }
        return ''
    }, [phone])

    const nodes = useNodesData([nodeId]) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                      allUpstreamNodes: nodes
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodes]
    )

    const userOption = useGetUserRecordOptions()

    const text = useMemo(() => {
        if (phone.type === VariableType.VALUE) {
            const result = phone?.valueVariable?.value || ''
            if (typeof result === 'string') {
                return result
            }
            if (isRichTextValue(result)) {
                return ''
            }
            if (typeof result === 'object') {
                return result?.join(',')
            }
            return typeof result === 'string' ? result : JSON.stringify(result)
        }
        return textRichVariableName({
            userOption,
            upstreamOptions,
            // pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
            data: phone
        })
    }, [phone, userOption, upstreamOptions])

    if (!text) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <CM.FieldItemContent>
                <CM.FieldItemTitle>
                    <SC.Text color={phone.type === VariableType.VALUE ? undefined : 'var(--color-purple-500)'}>{text}</SC.Text>
                </CM.FieldItemTitle>
            </CM.FieldItemContent>
        </CM.CardItemContainer>
    )
}

export default CallCard
